import React from 'react';
import { Phone, MessageCircle, Copy } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Normalise a phone for tel: and wa.me links.
// - tel: keeps + and digits.
// - wa.me wants digits only, with the country code. We assume Oman (+968) if number starts with 9 and is 8 digits.
const normalize = (phone) => {
  if (!phone) return { telHref: '', waHref: '', display: '' };
  const trimmed = phone.trim();
  // Keep + and digits only
  const tel = trimmed.replace(/[^\d+]/g, '');
  let digits = tel.replace(/\D/g, '');
  // Oman fallback: bare 8-digit mobile (9xxxxxxx) → prepend 968
  if (!tel.startsWith('+') && digits.length === 8 && digits.startsWith('9')) {
    digits = `968${digits}`;
  }
  return {
    telHref: `tel:${tel}`,
    waHref: `https://wa.me/${digits}`,
    display: trimmed,
  };
};

/**
 * PhoneActions
 * - Pill: shows the phone number, opens tel: on click (mobile dialer, desktop apps like FaceTime/Skype/etc.)
 * - WhatsApp icon button
 * - Copy icon button
 *
 * Props:
 *   phone: string
 *   size: 'sm' | 'md'  (md by default)
 *   stopPropagation: bool — set true when inside a clickable parent row
 */
const PhoneActions = ({ phone, size = 'md', stopPropagation = false }) => {
  const { toast } = useToast();
  if (!phone) return <span className="text-neutral-400">No phone</span>;

  const { telHref, waHref, display } = normalize(phone);
  const stop = (e) => {
    if (stopPropagation) e.stopPropagation();
  };

  const copy = async (e) => {
    stop(e);
    try {
      await navigator.clipboard.writeText(display);
      toast({ title: 'Copied', description: display });
    } catch {
      toast({ title: 'Copy failed' });
    }
  };

  const isSm = size === 'sm';
  const padX = isSm ? 'px-2' : 'px-2.5';
  const padY = isSm ? 'py-0.5' : 'py-1';
  const text = isSm ? 'text-[11px]' : 'text-xs';
  const iconSize = isSm ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const btnSize = isSm ? 'w-6 h-6' : 'w-7 h-7';

  return (
    <span className="inline-flex items-center gap-1.5" onClick={stop}>
      <a
        href={telHref}
        onClick={stop}
        className={`inline-flex items-center gap-1 ${padX} ${padY} rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold ${text} transition`}
        title={`Call ${display}`}
        data-testid={`phone-call-${display}`}
      >
        <Phone className={iconSize} />
        {display}
      </a>
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        onClick={stop}
        className={`inline-flex items-center justify-center ${btnSize} rounded-full bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 transition`}
        title={`WhatsApp ${display}`}
        data-testid={`phone-wa-${display}`}
      >
        <MessageCircle className={iconSize} />
      </a>
      <button
        type="button"
        onClick={copy}
        className={`inline-flex items-center justify-center ${btnSize} rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition`}
        title="Copy number"
        data-testid={`phone-copy-${display}`}
      >
        <Copy className={iconSize} />
      </button>
    </span>
  );
};

export default PhoneActions;
